import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit2,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { toast } from "sonner";
import Pagination from "../../ui/pagination";
import { InputFieldset } from "../../ui/inputFieldset";
import {
  EmploymentStatusApi,
  getEmploymentStatuses,
  createEmploymentStatus,
  updateEmploymentStatus,
  deleteEmploymentStatus,
} from "../../services/employmentStatusService";
import { FullScreenLoading } from "../../components/ui/loader";

interface EmploymentStatus {
  id: string;
  name: string;
  order: number;
  color: string;
  status: "active" | "inactive";
}

export function EmploymentStatusPage() {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState<EmploymentStatus | null>(null);
  const [employmentStatuses, setEmploymentStatuses] = useState<EmploymentStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<EmploymentStatus | null>( null,);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    order: 1,
    color: "#5B9BD5",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    name: "",
    order: "",
    color: "",
  });

  const fetchEmploymentStatuses = async () => {
    try {
      setLoading(true);
      const response = await getEmploymentStatuses({
        statusName: searchTerm.trim() || undefined,
        pageNo,
        pageSize,
      });
      const mappedData: EmploymentStatus[] = response.data.data.map(
        (item: EmploymentStatusApi) => ({
          id: item.publicId,
          name: item.statusName,
          order: item.displayOrder,
          color: item.color,
          status: item.isActive ? "active" : "inactive",
        }),
      );
      setEmploymentStatuses(mappedData);
      setTotalCount(response.data.totalRecords);
    } catch (error) {
      toast.error("Failed to load employment statuses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({
      title: "Employment Status",
      icon: <Users className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    fetchEmploymentStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    return [...employmentStatuses].sort((a, b) => {
      if (!sortColumn) return 0;
      const aValue = a[sortColumn as keyof EmploymentStatus];
      const bValue = b[sortColumn as keyof EmploymentStatus];
      if (typeof aValue === "string" && typeof bValue === "string") {
        const result = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortDirection === "asc" ? result : -result;
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [employmentStatuses, sortColumn, sortDirection]);

  const resetForm = () => {
    setFormData({
      name: "",
      order: 1,
      color: "#5B9BD5",
      status: "active",
    });

    setErrors({
      name: "",
      order: "",
      color: "",
    });

    setEditingItem(null);
    setIsEditMode(false);
  };

  const handleOpenForm = (item?: EmploymentStatus) => {
    if (item) {
      setIsEditMode(true);
      setEditingItem(item);
      setFormData({
        name: item.name,
        order: item.order,
        color: item.color,
        status: item.status,
      });
    } else {
      setIsEditMode(false);
      setEditingItem(null);
      setFormData({
        name: "",
        order: 1,
        color: "#5B9BD5",
        status: "active",
      });
    }
    setErrors({
      name: "",
      order: "",
      color: "",
    });
    setIsAddDialogOpen(true);
  };

  const handleSaveEmploymentStatus = async () => {
    const newErrors = {
      name: "",
      order: "",
      color: "",
    };
    if (!formData.name.trim()) {
      newErrors.name = "Status Name is required";
    } else if (formData.name.trim().length > 50) {
      newErrors.name = "Status Name can contain maximum 50 characters.";
    } else if (!/^[A-Za-z ]+$/.test(formData.name.trim())) {
      newErrors.name = "Status Name can contain only letters and spaces.";
    }
    if (
      formData.order === undefined ||
      formData.order === null ||
      Number.isNaN(Number(formData.order))
    ) {
      newErrors.order = "Display Order is required";
    } else if (Number(formData.order) <= 0) {
      newErrors.order = "Display Order must be greater than 0";
    } else if (!Number.isInteger(Number(formData.order))) {
      newErrors.order = "Display Order must be a whole number";
    }
    if (!formData.color.trim()) {
      newErrors.color = "Color is required";
    }
    const statusNameExists = employmentStatuses.some(
      (item) =>
        item.name.toLowerCase() === formData.name.trim().toLowerCase() &&
        item.id !== editingItem?.id,
    );
    if (statusNameExists) {
      newErrors.name =
        "Status Name already used for another employment status.";
    }
    const orderExists = employmentStatuses.some(
      (item) =>
        item.order === Number(formData.order) && item.id !== editingItem?.id,
    );
    if (orderExists) {
      newErrors.order =
        "Display Order already used for another employment status.";
    }
    setErrors(newErrors);
    if (newErrors.name || newErrors.order || newErrors.color) {
      return;
    }
    try {
      setSaving(true);
      const payload = {
        statusName: formData.name.trim(),
        displayOrder: Number(formData.order),
        color: formData.color,
        isActive: formData.status === "active",
      };
      if (isEditMode && editingItem) {
        const response = await updateEmploymentStatus(editingItem.id, payload);
        toast.success(
          response.message || "Employment status updated successfully.",
        );
      } else {
        const response = await createEmploymentStatus(payload);
        toast.success(
          response.message || "Employment status created successfully.",
        );
      }
      setIsAddDialogOpen(false);
      resetForm();
      await fetchEmploymentStatuses();
    } catch (error) {
      toast.error(
        isEditMode
          ? "Failed to update employment status."
          : "Failed to create employment status.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: EmploymentStatus) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;
    try {
      setDeleting(true);
      const response = await deleteEmploymentStatus(deletingItem.id);
      toast.success(
        response.message || "Employment status deleted successfully.",
      );
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
      await fetchEmploymentStatuses();
    } catch (error) {
      toast.error("Failed to delete employment status.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {(loading || saving || deleting) &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <FullScreenLoading
              title={
                deleting
                  ? "Deleting employment status..."
                  : saving
                    ? "Saving employment status..."
                    : "Loading employment statuses..."
              }
            />
          </div>,
          document.body,
        )}

      <div className="space-y-6 pb-8">
        {/* Search + Add */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

            <Input
              placeholder="Search by employment status"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10 pr-10"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => handleOpenForm()}
              style={{
                background: "#5B9BD5",
              }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Status
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{
            marginTop: "10px",
            gap: "0px",
          }}
        >
          <CardContent
            style={{
              padding: "0px 0px 10px 0px",
            }}
          >
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow
                    style={{
                      backgroundColor: "#E3F2FD",
                    }}
                  >
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Status Name
                        {sortColumn === "name" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("order")}
                    >
                      <div className="flex items-center gap-1">
                        Order
                        {sortColumn === "order" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>

                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs"
                    >
                      Color
                    </TableHead>

                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>

                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No employment statuses found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item) => (
                      <TableRow key={item.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {item.name}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.order}
                          </span>
                        </TableCell>

                        <TableCell className="px-4 py-2">
                          <span className="flex items-center gap-2 text-xs text-gray-600">
                            <i
                              className="h-5 w-5 rounded-full border border-gray-200"
                              style={{
                                background: item.color,
                              }}
                            />
                            {item.color}
                          </span>
                        </TableCell>

                        <TableCell className="px-4 py-2 text-center">
                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>

                        <TableCell className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenForm(item)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(item)}
                              className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No employment statuses found</p>
                </div>
              ) : (
                sortedData.map((item) => (
                  <Card
                    key={item.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent
                      style={{
                        padding: "14px",
                      }}
                    >
                      {/* Status Name */}
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {item.name}
                            </p>

                            <span className="text-xs text-gray-600">
                              Order: {item.order}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Color */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Color:</span>

                          <span className="flex items-center gap-2 text-sm text-gray-700">
                            <i
                              className="h-5 w-5 rounded-full border border-gray-200"
                              style={{
                                background: item.color,
                              }}
                            />
                            {item.color}
                          </span>
                        </div>
                      </div>

                      {/* Status + Actions */}
                      <div className="flex items-center justify-between border-t border-gray-100 mt-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>

                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenForm(item)}
                            className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                            title="Edit"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(item)}
                            className="h-8 text-xs text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="employment statuses"
          />
        </Card>

        {/* Add / Edit Dialog */}
        <Dialog
          open={isAddDialogOpen}
          onOpenChange={(open) => {
            setIsAddDialogOpen(open);

            if (!open) {
              resetForm();
            }
          }}
        >
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {isEditMode
                  ? "Edit Employment Status"
                  : "Add Employment Status"}
              </DialogTitle>

              <DialogDescription>
                {isEditMode
                  ? "Update employment status information"
                  : "Create a new employment status entry"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Name */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Status Name"
                    required
                    disabled={saving}
                    id="employment-status-name"
                    value={formData.name}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z ]/g, "")
                        .slice(0, 50);

                      setFormData({
                        ...formData,
                        name: value,
                      });

                      setErrors({
                        ...errors,
                        name: "",
                      });
                    }}
                    placeholder="e.g., Probation"
                    className={`text-sm ${
                      errors.name ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.name && (
                    <p className="text-red-500 text-xs">{errors.name}</p>
                  )}
                </div>

                {/* Display Order */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Display Order"
                    required
                    disabled={saving}
                    id="employment-status-order"
                    type="number"
                    min={1}
                    value={formData.order}
                    onChange={(e) => {
                      const value = e.target.value;

                      setFormData({
                        ...formData,
                        order: value === "" ? 0 : Number(value),
                      });

                      setErrors({
                        ...errors,
                        order: "",
                      });
                    }}
                    placeholder="e.g., 1"
                    className={`text-sm ${
                      errors.order ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.order && (
                    <p className="text-red-500 text-xs">{errors.order}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Color */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Color"
                    required
                    disabled={saving}
                    id="employment-status-color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        color: e.target.value,
                      });

                      setErrors({
                        ...errors,
                        color: "",
                      });
                    }}
                    className={`text-sm ${
                      errors.color ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.color && (
                    <p className="text-red-500 text-xs">{errors.color}</p>
                  )}
                </div>

                {/* Status Switch */}
                <div className="min-w-0 flex flex-col justify-center gap-2 px-3 py-2">
                  <label
                    htmlFor="employment-status-status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Status
                  </label>

                  <div className="flex items-center gap-3">
                    <Switch
                      id="employment-status-status"
                      disabled={saving}
                      checked={formData.status === "active"}
                      onCheckedChange={(checked) =>
                        setFormData((current) => ({
                          ...current,
                          status: checked ? "active" : "inactive",
                        }))
                      }
                    />

                    <span className="text-sm text-gray-700">
                      {formData.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={handleSaveEmploymentStatus}
                disabled={saving}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                {isEditMode ? "Update Status" : "Add Status"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Employment Status
              </DialogTitle>

              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">"{deletingItem?.name}"</span>?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>

              <Button
                onClick={confirmDelete}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
